import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, Truck, Loader2 } from 'lucide-react';
import { addDays, ymd, errMsg } from '../porters/porterUi';

const mondayOf = (s) => { const d = new Date(s + 'T00:00:00'); return addDays(s, -((d.getDay() + 6) % 7)); };
const lbl = (s, o) => new Date(s + 'T00:00:00').toLocaleDateString('en-GB', o);
const vanName = (v) => (v ? (v.name ? `${v.name} · ${v.reg}` : v.reg) : '');

export const VanRota = ({ authFetch }) => {
  const [start, setStart] = useState(mondayOf(ymd(new Date())));
  const [d, setD] = useState(null);
  const load = useCallback(() => authFetch('get', `/admin/van-rota?start=${start}&days=7`).then(({ data }) => setD(data)).catch((e) => toast.error(errMsg(e))), [authFetch, start]);
  useEffect(() => { load(); }, [load]);
  const set = async (driver, dates, van_id) => {
    try {
      await authFetch('put', '/admin/van-rota', { driver_id: driver.id, dates, van_id: van_id || null });
      toast.success(van_id ? `${driver.name}: van set for ${dates.length === 7 ? 'the week' : lbl(dates[0], { weekday: 'short', day: 'numeric' })}.` : `${driver.name}: van cleared.`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const sel = 'h-9 w-full rounded-md border border-slate-200 bg-white text-xs px-1';
  return (
    <section className="mb-6 rounded-xl border border-slate-200 bg-white p-4" data-testid="van-rota">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <h2 className="font-display text-xl font-extrabold text-[color:var(--mv-navy)] flex items-center gap-2 flex-1"><Truck className="w-5 h-5 text-[color:var(--mv-orange)]" />Van rota</h2>
        <button type="button" onClick={() => setStart(addDays(start, -7))} className="h-9 w-9 rounded-md border border-slate-200 bg-white inline-flex items-center justify-center" aria-label="Previous week" data-testid="van-rota-prev"><ChevronLeft className="w-4 h-4" /></button>
        <div className="text-sm font-black text-[color:var(--mv-navy)]" data-testid="van-rota-week">{lbl(start, { day: 'numeric', month: 'short' })} – {lbl(addDays(start, 6), { day: 'numeric', month: 'short' })}</div>
        <button type="button" onClick={() => setStart(addDays(start, 7))} className="h-9 w-9 rounded-md border border-slate-200 bg-white inline-flex items-center justify-center" aria-label="Next week" data-testid="van-rota-next"><ChevronRight className="w-4 h-4" /></button>
      </div>
      <div className="text-xs text-slate-500 mb-2">Pick a van for the whole week, then change any single day. Each driver's jobs use the van they have that day.</div>
      {!d ? <Loader2 className="w-5 h-5 animate-spin" /> : d.vans.length === 0 ? <div className="text-sm text-slate-500">No vans yet. Add vans under Driver reports → Vans.</div> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[860px]">
            <thead><tr className="text-[10px] uppercase text-slate-500">
              <th className="text-left py-1 pr-2">Driver</th><th className="py-1 px-1">Whole week</th>
              {d.dates.map(x => <th key={x} className="py-1 px-1">{lbl(x, { weekday: 'short', day: 'numeric' })}</th>)}
            </tr></thead>
            <tbody>
              {d.drivers.map(dr => {
                const row = d.rota[dr.id] || {};
                const vals = d.dates.map(x => row[x] || '');
                const weekVal = vals.every(v => v === vals[0]) ? vals[0] : '__mixed';
                return (
                  <tr key={dr.id} className="border-t border-slate-100" data-testid={`van-rota-row-${dr.id.slice(0, 6)}`}>
                    <td className="py-1.5 pr-2 font-bold whitespace-nowrap" style={{ color: dr.color }}>{dr.name}</td>
                    <td className="py-1.5 px-1">
                      <select value={weekVal} onChange={(e) => set(dr, d.dates, e.target.value)} className={`${sel} font-bold`} data-testid={`van-rota-week-${dr.id.slice(0, 6)}`}>
                        {weekVal === '__mixed' && <option value="__mixed" disabled>Mixed</option>}
                        <option value="">No van</option>
                        {d.vans.map(v => <option key={v.id} value={v.id}>{vanName(v)}{v.off_road ? ' (off road)' : ''}</option>)}
                      </select>
                    </td>
                    {d.dates.map((x, i) => (
                      <td key={x} className="py-1.5 px-1">
                        <select value={vals[i]} onChange={(e) => set(dr, [x], e.target.value)} className={`${sel} ${vals[i] ? 'bg-sky-50 border-sky-200' : ''}`} data-testid={`van-rota-${dr.id.slice(0, 6)}-${x}`}>
                          <option value="">—</option>
                          {d.vans.map(v => <option key={v.id} value={v.id}>{v.name || v.reg}</option>)}
                        </select>
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};
